import * as React from "react"

import { useMotionPreference } from "../utils/motion-preference"

const DOT_SPACING = 10
const DOT_RADIUS = 0.55
const LENS_RADIUS = 190
const EVENT_HORIZON_RADIUS = 36
const SWALLOW_RADIUS = 24
const ACCRETION_RING_RADIUS = 58
const ACCRETION_RING_WIDTH = 13
const GRAVITY_PULL_STRENGTH = 78
const LENS_SWIRL_STRENGTH = 42
const ORBIT_DOT_COUNT = 64
const ORBIT_TILT = 0.5
const IDLE_HOLD_MS = 140
const RETURN_DURATION_MS = 1050
const POINTER_FOLLOW_EASE = 0.58
const TAU = Math.PI * 2
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const smoothstep = (edge0, edge1, value) => {
  const progress = clamp((value - edge0) / (edge1 - edge0), 0, 1)
  return progress * progress * (3 - 2 * progress)
}

const pseudoRandom = (x, y) => {
  const value = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453
  return value - Math.floor(value)
}

const InteractiveDotBackground = () => {
  const canvasRef = React.useRef(null)
  const { reduceMotion } = useMotionPreference()

  React.useEffect(() => {
    if (reduceMotion || typeof window === "undefined") return undefined

    const canvas = canvasRef.current
    const context = canvas?.getContext("2d", { alpha: true })
    if (!canvas || !context) return undefined

    let frameId = null
    let width = 0
    let height = 0
    let dpr = 1
    const bufferCanvas = document.createElement("canvas")
    const bufferContext = bufferCanvas.getContext("2d", { alpha: true })
    let targetPointer = null
    let pointer = null
    let lastPointerTime = 0

    if (!bufferContext) return undefined

    document.documentElement.classList.add("has-interactive-dot-background")

    const drawDot = (targetContext, x, y, alpha, size = 1) => {
      targetContext.globalAlpha = alpha
      targetContext.fillRect(
        Math.round(x - size * DOT_RADIUS),
        Math.round(y - size * DOT_RADIUS),
        size,
        size
      )
    }

    const drawStaticDots = (targetContext, left, top, right, bottom) => {
      const startX = Math.floor(left / DOT_SPACING) * DOT_SPACING
      const startY = Math.floor(top / DOT_SPACING) * DOT_SPACING
      const endX = Math.ceil(right / DOT_SPACING) * DOT_SPACING
      const endY = Math.ceil(bottom / DOT_SPACING) * DOT_SPACING

      targetContext.fillStyle = "#d9d9d9"

      for (let y = startY; y <= endY; y += DOT_SPACING) {
        for (let x = startX; x <= endX; x += DOT_SPACING) {
          drawDot(targetContext, x, y, (x + y) % 20 === 0 ? 0.14 : 0.075)
        }
      }

      targetContext.globalAlpha = 1
    }

    const drawSingularityOverlay = (targetContext, centerX, centerY, strength, now) => {
      targetContext.save()
      targetContext.globalAlpha = strength

      const coreRadius = EVENT_HORIZON_RADIUS * 1.55
      const coreGradient = targetContext.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        coreRadius
      )
      coreGradient.addColorStop(0, "rgba(0, 0, 0, 1)")
      coreGradient.addColorStop(0.62, "rgba(0, 0, 0, 1)")
      coreGradient.addColorStop(1, "rgba(0, 0, 0, 0)")

      targetContext.fillStyle = coreGradient
      targetContext.fillRect(
        centerX - coreRadius,
        centerY - coreRadius,
        coreRadius * 2,
        coreRadius * 2
      )
      targetContext.restore()

      targetContext.save()
      targetContext.translate(centerX, centerY)
      targetContext.rotate(Math.sin(now * 0.00042) * 0.18)
      targetContext.scale(1, ORBIT_TILT)
      targetContext.lineWidth = 1
      targetContext.globalAlpha = strength
      targetContext.strokeStyle = "rgba(217, 217, 217, 0.12)"
      targetContext.beginPath()
      targetContext.arc(0, 0, ACCRETION_RING_RADIUS, 0, Math.PI * 2)
      targetContext.stroke()

      targetContext.lineWidth = 1.5
      targetContext.strokeStyle = "rgba(217, 217, 217, 0.2)"
      targetContext.beginPath()
      targetContext.arc(
        0,
        0,
        ACCRETION_RING_RADIUS + 2,
        -0.2 + now * 0.0012,
        Math.PI * 0.74 + now * 0.0012
      )
      targetContext.stroke()
      targetContext.restore()

      targetContext.fillStyle = "#d9d9d9"
      for (let index = 0; index < ORBIT_DOT_COUNT; index += 1) {
        const fallProgress = (index / ORBIT_DOT_COUNT + now * 0.00022) % 1
        const swallowFade = 1 - smoothstep(0.78, 1, fallProgress)
        const arrivalFade = smoothstep(0, 0.18, fallProgress)
        const angle = index * GOLDEN_ANGLE + now * 0.00145 + fallProgress * 2.65
        const radius = EVENT_HORIZON_RADIUS + (1 - fallProgress) ** 1.45 * 74
        const x = centerX + Math.cos(angle) * radius
        const y = centerY + Math.sin(angle) * radius * ORBIT_TILT
        const frontLight = smoothstep(-0.6, 1, Math.sin(angle))
        const alpha = strength * arrivalFade * swallowFade * (0.05 + frontLight * 0.18)
        const size = 1 + frontLight * 0.9

        drawDot(targetContext, x, y, alpha, Math.round(size))
      }

      targetContext.globalAlpha = 1
    }

    const paintStaticBuffer = () => {
      bufferContext.clearRect(0, 0, width, height)
      bufferContext.fillStyle = "#050505"
      bufferContext.fillRect(0, 0, width, height)
      drawStaticDots(
        bufferContext,
        -DOT_SPACING,
        -DOT_SPACING,
        width + DOT_SPACING,
        height + DOT_SPACING
      )
    }

    const paintCachedBackground = () => {
      context.clearRect(0, 0, width, height)
      context.globalAlpha = 1
      context.drawImage(bufferCanvas, 0, 0, width, height)
    }

    const requestDraw = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(draw)
    }

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2)
      width = window.innerWidth
      height = window.innerHeight
      canvas.width = Math.max(1, Math.round(width * dpr))
      canvas.height = Math.max(1, Math.round(height * dpr))
      bufferCanvas.width = canvas.width
      bufferCanvas.height = canvas.height
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      context.setTransform(dpr, 0, 0, dpr, 0, 0)
      bufferContext.setTransform(dpr, 0, 0, dpr, 0, 0)
      paintStaticBuffer()
      paintCachedBackground()
    }

    const draw = () => {
      frameId = null
      const now = window.performance.now()
      paintCachedBackground()

      const idleTime = now - lastPointerTime
      const returnProgress = Math.min(
        1,
        Math.max(0, (idleTime - IDLE_HOLD_MS) / RETURN_DURATION_MS)
      )
      const influenceStrength = (1 - returnProgress) ** 2

      if (targetPointer) {
        pointer = pointer
          ? {
              x: pointer.x + (targetPointer.x - pointer.x) * POINTER_FOLLOW_EASE,
              y: pointer.y + (targetPointer.y - pointer.y) * POINTER_FOLLOW_EASE,
            }
          : { ...targetPointer }
      } else if (pointer && influenceStrength <= 0.01) {
        pointer = null
      }

      const influencePointer = pointer && influenceStrength > 0.01 ? pointer : null
      const radiusSquared = LENS_RADIUS * LENS_RADIUS

      if (influencePointer) {
        const repaintRadius = LENS_RADIUS + LENS_SWIRL_STRENGTH + DOT_SPACING * 3
        const repaintLeft = Math.max(-DOT_SPACING, influencePointer.x - repaintRadius)
        const repaintTop = Math.max(-DOT_SPACING, influencePointer.y - repaintRadius)
        const repaintRight = Math.min(width + DOT_SPACING, influencePointer.x + repaintRadius)
        const repaintBottom = Math.min(height + DOT_SPACING, influencePointer.y + repaintRadius)

        context.save()
        context.beginPath()
        context.rect(repaintLeft, repaintTop, repaintRight - repaintLeft, repaintBottom - repaintTop)
        context.clip()
        context.fillStyle = "#050505"
        context.fillRect(repaintLeft, repaintTop, repaintRight - repaintLeft, repaintBottom - repaintTop)
        context.fillStyle = "#d9d9d9"
        context.strokeStyle = "#d9d9d9"
        context.lineWidth = 1

        const startX = Math.floor(repaintLeft / DOT_SPACING) * DOT_SPACING
        const startY = Math.floor(repaintTop / DOT_SPACING) * DOT_SPACING
        const endX = Math.ceil(repaintRight / DOT_SPACING) * DOT_SPACING
        const endY = Math.ceil(repaintBottom / DOT_SPACING) * DOT_SPACING

        for (let y = startY; y <= endY; y += DOT_SPACING) {
          for (let x = startX; x <= endX; x += DOT_SPACING) {
            let drawX = x
            let drawY = y
            let alpha = (x + y) % 20 === 0 ? 0.14 : 0.075
            let dotSize = 1

            const dx = x - influencePointer.x
            const dy = y - influencePointer.y
            const distanceSquared = dx * dx + dy * dy

            if (distanceSquared < radiusSquared && distanceSquared > 0.01) {
              const distance = Math.sqrt(distanceSquared)
              const normalizedDistance = distance / LENS_RADIUS
              const unitX = dx / distance
              const unitY = dy / distance
              const dotSeed = pseudoRandom(x, y)
              const flowPhase = now * 0.00155 + dotSeed * TAU + distance * 0.045
              const streamPulse = 0.72 + Math.sin(flowPhase) * 0.28
              const lensForce = (1 - normalizedDistance) ** 1.7 * influenceStrength
              const ringDistance = (distance - ACCRETION_RING_RADIUS) / ACCRETION_RING_WIDTH
              const ringForce = Math.exp(-(ringDistance * ringDistance)) * influenceStrength
              const inwardPull = GRAVITY_PULL_STRENGTH * lensForce * (0.65 + (1 - normalizedDistance) * 0.9) * streamPulse
              const targetDistance = distance - inwardPull
              const swallowedFade = smoothstep(SWALLOW_RADIUS, EVENT_HORIZON_RADIUS * 1.3, targetDistance)
              const swirlDisplacement = LENS_SWIRL_STRENGTH * lensForce * swallowedFade * (0.65 + ringForce)
              const streamFlutter = Math.sin(flowPhase * 1.7) * lensForce * 5

              drawX -= unitX * inwardPull
              drawY -= unitY * inwardPull
              drawX += -unitY * (swirlDisplacement + streamFlutter)
              drawY += unitX * (swirlDisplacement + streamFlutter)

              alpha = (alpha * (0.52 + lensForce * 0.78) + ringForce * 0.28) * swallowedFade
              dotSize += ringForce * 2 + lensForce * 0.45

              const displacement = Math.hypot(drawX - x, drawY - y)
              if (displacement > 3 && alpha > 0.018) {
                context.globalAlpha = alpha * 0.34 * smoothstep(0, 34, displacement)
                context.beginPath()
                context.moveTo(x, y)
                context.lineTo(drawX, drawY)
                context.stroke()
              }
            }

            if (alpha > 0.005) {
              drawDot(context, drawX, drawY, alpha, Math.max(1, Math.round(dotSize)))
            }
          }
        }

        drawSingularityOverlay(context, influencePointer.x, influencePointer.y, influenceStrength, now)
        context.restore()
      }

      context.globalAlpha = 1

      if (influenceStrength > 0.01) {
        frameId = window.requestAnimationFrame(draw)
      }
    }

    const handlePointerMove = event => {
      targetPointer = { x: event.clientX, y: event.clientY }
      lastPointerTime = window.performance.now()
      requestDraw()
    }

    const handleTouch = event => {
      const touch = event.touches[0] || event.changedTouches[0]
      if (!touch) return

      targetPointer = { x: touch.clientX, y: touch.clientY }
      lastPointerTime = window.performance.now()
      requestDraw()
    }

    const handlePointerLeave = () => {
      targetPointer = null
      lastPointerTime = window.performance.now()
      requestDraw()
    }

    const handleTouchEnd = event => {
      if (event.touches.length > 0) {
        handleTouch(event)
        return
      }

      handlePointerLeave()
    }

    resize()
    requestDraw()

    window.addEventListener("resize", resize)
    window.addEventListener("pointerdown", handlePointerMove, { passive: true })
    window.addEventListener("pointermove", handlePointerMove, { passive: true })
    window.addEventListener("pointerleave", handlePointerLeave)
    window.addEventListener("touchstart", handleTouch, { passive: true })
    window.addEventListener("touchmove", handleTouch, { passive: true })
    window.addEventListener("touchend", handleTouchEnd, { passive: true })
    window.addEventListener("touchcancel", handlePointerLeave)
    window.addEventListener("blur", handlePointerLeave)

    return () => {
      document.documentElement.classList.remove("has-interactive-dot-background")
      if (frameId !== null) window.cancelAnimationFrame(frameId)
      window.removeEventListener("resize", resize)
      window.removeEventListener("pointerdown", handlePointerMove)
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerleave", handlePointerLeave)
      window.removeEventListener("touchstart", handleTouch)
      window.removeEventListener("touchmove", handleTouch)
      window.removeEventListener("touchend", handleTouchEnd)
      window.removeEventListener("touchcancel", handlePointerLeave)
      window.removeEventListener("blur", handlePointerLeave)
    }
  }, [reduceMotion])

  if (reduceMotion) return null

  return (
    <canvas
      ref={canvasRef}
      className="interactive-dot-background"
      aria-hidden="true"
    />
  )
}

export default InteractiveDotBackground
